import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PreviewBatchGeneratorPresets from '../components/previews/PreviewBatchGeneratorPresets';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  Save,
  Edit,
  ChevronsUp,
  ImagePlay,
  X,
  Loader2,
  Layers,
} from 'lucide-react';
import { useNotification } from '../hooks/useNotification';
import LoadingButton from '../components/LoadingButton';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';
import { getImageUrl } from '../utils/getImageUrl';
import BookNavigationTabs from '../components/BookNavigationTabs';
import BulkLayoutTemplateApply from '../components/BulkLayoutTemplateApply';

const LANGUAGE_PAIR_OPTIONS = [
  'es_de',
  'es_en',
  'es_ru',
  'es_it',
  'es_tr',
  'es_fr',
  'es_pl',
  'de_nl',
  'de_ru',
  'de_en',
  'de_it',
  'de_fr',
  'de_tr',
  'de_pl',
  'en_nl',
  'en_pl',
  'en_it',
  'en_fr',
  'en_tr',
  'en_ru',
].map((value) => ({
  value,
  label: value.replace('_', '-').toUpperCase(),
}));

function BulkEditorPagePresets() {
  const languageFieldMap = {
    text_en: 'English',
    text_de: 'German',
    text_es: 'Spanish',
    text_nl: 'Dutch',
    text_ru: 'Russian',
    text_it: 'Italian',
    text_fr: 'French',
    text_tr: 'Turkish',
    text_pl: 'Polish',
  };
  const location = useLocation();
  const { bookId } = useParams();
  const [pages, setPages] = useState([]);
  const [additionalEntriesByPageId, setAdditionalEntriesByPageId] = useState({});
  const [savingAdditionalFields, setSavingAdditionalFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageLanguage, setPageLanguage] = useState(() => new URLSearchParams(location.search).get('plang') || 'es_de');
  const [startBulk, setStartBulk] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { notify, Notification } = useNotification();
  const [jumpToPage, setJumpToPage] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [bookName, setBookName] = useState('');
  const [pagesToProcess, setPagesToProcess] = useState([]);
  const [pageLayoutBoxCounts, setPageLayoutBoxCounts] = useState({});
  const [favoriteLanguagePairs, setFavoriteLanguagePairs] = useState([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [showPagesWithoutText, setShowPagesWithoutText] = useState(true);

  // Page render controls
  const [showRenderPanel, setShowRenderPanel] = useState(false);
  const [useAllPages, setUseAllPages] = useState(true);
  const [applyingLayout, setApplyingLayout] = useState(false);
  const [copiedLayoutVersions, setCopiedLayoutVersions] = useState({});
  const layoutTargetPages = useMemo(() => useAllPages ? pages : pages.filter((page) => selectedIds.includes(page._id)), [pages, selectedIds, useAllPages]);
  const adoptCopiedLayout = (page) => {
    setCopiedLayoutVersions((current) => ({ ...current, [page._id]: page.layout_version }));
    setPageLayoutBoxCounts((current) => ({ ...current, [page._id]: (page.text_items || []).filter((item) => item.visibleOnCanvas !== false).length }));
    setAdditionalEntriesByPageId((current) => ({ ...current, [page._id]: (page.page_text_entries || []).map((entry) => ({
      ...entry, texts: current[page._id]?.find((local) => local.id === entry.id)?.texts || entry.texts || {},
    })) }));
  };
  const favoriteLanguagePairSet = useMemo(
    () => new Set(favoriteLanguagePairs),
    [favoriteLanguagePairs],
  );
  const sortedLanguagePairOptions = useMemo(() => {
    const orderMap = new Map(
      LANGUAGE_PAIR_OPTIONS.map((option, index) => [option.value, index]),
    );

    return [...LANGUAGE_PAIR_OPTIONS].sort((a, b) => {
      const aFavorite = favoriteLanguagePairSet.has(a.value);
      const bFavorite = favoriteLanguagePairSet.has(b.value);
      if (aFavorite !== bFavorite) {
        return aFavorite ? -1 : 1;
      }
      return orderMap.get(a.value) - orderMap.get(b.value);
    });
  }, [favoriteLanguagePairSet]);

  useEffect(() => {
    const loadFavorites = async () => {
      if (!bookId) {
        setFavoriteLanguagePairs([]);
        setFavoritesLoaded(true);
        return;
      }

      setFavoritesLoaded(false);
      setFavoriteLanguagePairs([]);
      try {
        const response = await axios.get(
          `/api/book-favorite-language-pairs?book_id=${encodeURIComponent(bookId)}`,
          { withCredentials: true },
        );
        const favorites = response.data?.favorite_language_pairs || [];
        setFavoriteLanguagePairs(
          favorites.filter((value) =>
            LANGUAGE_PAIR_OPTIONS.some((option) => option.value === value),
          ),
        );
      } catch (err) {
        console.error('Failed to load favorite language pairs', err);
      } finally {
        setFavoritesLoaded(true);
      }
    };

    loadFavorites();
  }, [bookId]);

  useEffect(() => {
    if (!favoritesLoaded) return;

    const saveFavorites = async () => {
      try {
        await axios.put(
          '/api/book-favorite-language-pairs',
          {
            book_id: bookId,
            favorite_language_pairs: favoriteLanguagePairs,
          },
          { withCredentials: true },
        );
      } catch (err) {
        console.error('Failed to persist favorite language pairs', err);
      }
    };

    saveFavorites();
  }, [bookId, favoriteLanguagePairs, favoritesLoaded]);

  useEffect(() => {
    if (!favoritesLoaded) return;
    const filtered = favoriteLanguagePairs.filter((value) =>
      LANGUAGE_PAIR_OPTIONS.some((option) => option.value === value),
    );
    if (filtered.length !== favoriteLanguagePairs.length) {
      setFavoriteLanguagePairs(filtered);
    }
  }, [favoriteLanguagePairs, favoritesLoaded]);

  const toggleFavoriteLanguagePair = (languagePair) => {
    if (!languagePair) return;

    setFavoriteLanguagePairs((prev) =>
      prev.includes(languagePair)
        ? prev.filter((item) => item !== languagePair)
        : [...prev, languagePair],
    );
  };

  const updateAdditionalEntryText = (pageId, entryId, language, value) => {
    setAdditionalEntriesByPageId((current) => ({
      ...current,
      [pageId]: (current[pageId] || []).map((entry) => entry.id === entryId
        ? { ...entry, texts: { ...(entry.texts || {}), [language]: value } }
        : entry),
    }));
  };

  const saveAdditionalEntryLanguage = async (pageId, entryId, language) => {
    const entry = (additionalEntriesByPageId[pageId] || []).find((item) => item.id === entryId);
    if (!entry) return;
    const savingKey = `${pageId}:${entryId}:${language}`;
    setSavingAdditionalFields((current) => ({ ...current, [savingKey]: true }));
    try {
      const response = await axios.put(
        `/api/page/${pageId}/text-entries/${entryId}/${language}`,
        { text: entry.texts?.[language] || '' },
        { withCredentials: true },
      );
      setAdditionalEntriesByPageId((current) => ({
        ...current,
        [pageId]: (current[pageId] || []).map((item) =>
          item.id === entryId ? response.data?.page_text_entry || item : item,
        ),
      }));
      notify(`${languageFieldMap[`text_${language}`]} additional text saved.`);
    } catch (saveError) {
      console.error(`Failed to save extra text for page ${pageId}`, saveError);
      notify('Failed to save additional text.', 'error');
    } finally {
      setSavingAdditionalFields((current) => ({ ...current, [savingKey]: false }));
    }
  };

  const renderLanguagePairOptions = () =>
    sortedLanguagePairOptions.map((option) => {
      const isFavorite = favoriteLanguagePairSet.has(option.value);
      return (
        <option key={option.value} value={option.value}>
          {isFavorite ? '★ ' : ''}
          {option.label}
        </option>
      );
    });

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const url = showPagesWithoutText
          ? `/api/list_content_pages/${bookId}?plang=${pageLanguage}&include_empty=1`
          : `/api/list_content_pages/${bookId}?plang=${pageLanguage}`;
        const response = await axios.get(url);
        setPages(response.data);
      } catch (err) {
        setError('Failed to fetch pages.');
      } finally {
        setLoading(false);
      }
    };
    if (bookId && pageLanguage) {
      setLoading(true);
      fetchPages();
    }
  }, [bookId, pageLanguage, refreshCounter, showPagesWithoutText]);

  useEffect(() => {
    if (pages.length === 0) {
      setAdditionalEntriesByPageId({});
      setPageLayoutBoxCounts({});
      return;
    }
    let cancelled = false;
    Promise.all(pages.map(async (page) => {
      try {
        const [entriesResponse, itemsResponse] = await Promise.all([
          axios.get(`/api/page/${page._id}/text-entries`, { withCredentials: true }),
          page.page_text_box_count === undefined
            ? axios.get(`/api/page/${page._id}/text-items`, { withCredentials: true }).catch(() => null)
            : Promise.resolve(null),
        ]);
        const textItems = itemsResponse?.data?.text_items || [];
        const pageLayoutBoxCount = page.page_layout_box_count ?? page.page_text_box_count ?? textItems.filter((item) => item?.visibleOnCanvas !== false).length;
        return {
          pageId: page._id,
          entries: entriesResponse.data?.page_text_entries || [],
          pageLayoutBoxCount,
        };
      } catch (fetchError) {
        console.error(`Failed to fetch page metadata for ${page._id}`, fetchError);
        return { pageId: page._id, entries: [], pageLayoutBoxCount: page.page_layout_box_count ?? page.page_text_box_count ?? 0 };
      }
    })).then((results) => {
      if (!cancelled) {
        setAdditionalEntriesByPageId(Object.fromEntries(results.map(({ pageId, entries }) => [pageId, entries])));
        setPageLayoutBoxCounts(Object.fromEntries(results.map(({ pageId, pageLayoutBoxCount }) => [pageId, pageLayoutBoxCount])));
      }
    });
    return () => { cancelled = true; };
  }, [pages]);

  useEffect(() => {
    const fetchBookName = async () => {
      try {
        const response = await axios.get(
          `/api/list_pages/${bookId}?plang=${pageLanguage}`,
        );
        setBookName(response.data.book_name);
      } catch (err) {
        console.error('Failed to fetch book name:', err);
      }
    };
    if (bookId) {
      fetchBookName();
    }
  }, [bookId, pageLanguage]);



  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');
    if (langParam) setPageLanguage(langParam);
  }, [location.search]);

  // Restore scroll position when returning from an edit page
  useEffect(() => {
    if (pages.length === 0) return;
    const savedPage = sessionStorage.getItem('returnToPage');
    if (!savedPage) return;
    sessionStorage.removeItem('returnToPage');
    setJumpToPage(savedPage);
    const timer = setTimeout(() => {
      const element = document.getElementById(`page-row-${savedPage}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [pages]);

  // Track scroll position to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const startManualGeneration = async (targetPagesArg) => {
    if (applyingLayout) return;
    const targetPages =
      targetPagesArg ||
      (useAllPages ? pages : pages.filter((page) => selectedIds.includes(page._id)));

    if (targetPages.length === 0) {
      notify('Please select at least one page.', 'error');
      return;
    }

    setPagesToProcess(targetPages);
    setIsProcessing(true);
    setStartBulk(true);

    setShowRenderPanel(false);
  };

  const handleSaveText = async (pageId, field, value) => {
    if (!value.trim()) {
      const confirmed = window.confirm(
        'Are you sure you want to insert empty text?',
      );
      if (!confirmed) return;
      value = '';
    }

    try {
      const response = await axios.post(`/api/update_text`, {
        page_id: pageId,
        field,
        new_text: value,
      });
      if (response.status === 200) {
        notify(`Text saved`);
      } else {
        notify(`Content already exists`, 'error');
      }
    } catch (error) {
      notify(`Text Updated Failed`, 'error');
    }
  };

  const toggleSelectAll = () => {
    if (applyingLayout) return;
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pages.map((p) => p._id));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectOne = (id) => {
    if (applyingLayout) return;
    if (selectedIds.includes(id)) {
      setSelectedIds((prev) => prev.filter((pid) => pid !== id));
    } else {
      setSelectedIds((prev) => [...prev, id]);
    }
  };

  const handleJumpToPage = (pageNumber) => {
    if (!pageNumber) return;
    const element = document.getElementById(`page-row-${pageNumber}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash highlight effect
      element.classList.add('bg-yellow-200');
      setTimeout(() => element.classList.remove('bg-yellow-200'), 2000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Remove the saved page render for the active language pair
  const removePageRender = async (pageId) => {
    const previewKey = `image_preview_${pageLanguage}`;
    const confirmReset = window.confirm(
      `Remove this saved page render? The original artwork and private layout will be kept.`,
    );

    if (!confirmReset) return;

    try {
      const response = await axios.post(`/api/remove_preview_image`, {
        page_id: pageId,
        field: previewKey,
      });

      if (response.data.success) {
        notify('Page render removed.');

        // Update the pages state to reflect the change
        setPages((prevPages) =>
          prevPages.map((p) =>
            p._id === pageId ? { ...p, [previewKey]: null } : p,
          ),
        );
      } else {
        notify("Image could not be removed or doesn't exist.", 'error');
      }
    } catch (error) {
      console.error('Error removing page render:', error);
      notify('Failed to remove page render.', 'error');
    }
  };

  return (
    <>
      <Notification />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 left-6 z-50 bg-gray-900 text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-all duration-300"
          title="Scroll to top"
        >
          <ChevronsUp size={24} />
        </button>
      )}

      <div className="max-w-[1400px] mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
        <BookNavigationTabs
          bookId={bookId}
          currentLanguage={pageLanguage}
          headerTitle={bookName}
          headerLinkTo={`/books/${bookId}/edit`}
          showBackButton
          showBookSwitcher
          languageSelector={
            <>
              <span className="text-sm text-gray-600">Current Profile:</span>
              <div className="flex items-center gap-2">
                <select
                  value={pageLanguage}
                  disabled={applyingLayout}
                  onChange={(e) => setPageLanguage(e.target.value)}
                  className="border border-gray-300 rounded py-1 px-2 text-sm"
                >
                  {renderLanguagePairOptions()}
                </select>
                <button
                  type="button"
                  onClick={() => toggleFavoriteLanguagePair(pageLanguage)}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded border text-sm transition-colors ${
                    favoriteLanguagePairSet.has(pageLanguage)
                      ? 'border-amber-400 bg-amber-100 text-amber-700'
                      : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                  title={
                    favoriteLanguagePairSet.has(pageLanguage)
                      ? 'Remove favorite'
                      : 'Mark as favorite'
                  }
                  aria-label={
                    favoriteLanguagePairSet.has(pageLanguage)
                      ? 'Remove favorite language pair'
                      : 'Mark language pair as favorite'
                  }
                >
                  ★
                </button>
              </div>
            </>
          }
        />

        {/* Sticky Navigation Bar - right aligned */}
        <div className="sticky top-16 z-40 mb-4 flex justify-end">
          <div className="bg-white border border-gray-300 shadow-md rounded-lg px-4 py-2 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={showPagesWithoutText}
                disabled={applyingLayout}
                onChange={(e) => setShowPagesWithoutText(e.target.checked)}
                className="accent-gray-900"
              />
              Show pages without text
            </label>
            <div className="h-5 w-px bg-gray-200" />
            <label className="text-sm font-medium text-gray-700">
              Jump to:
            </label>
            <select
              value={jumpToPage}
              onChange={(e) => {
                setJumpToPage(e.target.value);
                handleJumpToPage(e.target.value);
              }}
              className="border border-gray-300 rounded py-1 px-3 text-sm"
            >
              <option value="">Select page...</option>
              {pages.map((page) => (
                <option key={page._id} value={page.page_number}>
                  Page {page.page_number} - {page.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3 text-sm text-gray-500">
          {showPagesWithoutText
            ? 'Showing all pages, including pages without text for this language pair.'
            : 'Showing only pages that have text for this language pair.'}
        </div>

        {/* Left floating trigger button */}
        <button
          onClick={() => setShowRenderPanel(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-gray-900 text-white flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-r-lg shadow-lg hover:bg-gray-700 transition-colors"
          title="Generate page renders"
          style={{ writingMode: 'vertical-rl' }}
        >
          <ImagePlay size={20} style={{ writingMode: 'horizontal-tb' }} />
          <span className="text-xs font-medium tracking-wide" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            Generate page renders
          </span>
        </button>

        {/* Side panel overlay backdrop */}
        {showRenderPanel && (
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => { if (!applyingLayout) setShowRenderPanel(false); }}
          />
        )}

        {/* Slide-in side panel */}
        <div
          inert={!showRenderPanel}
          aria-hidden={!showRenderPanel}
          className={`fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${showRenderPanel ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <ImagePlay size={18} className="text-gray-700" />
              <span className="font-semibold text-gray-800">Generate page renders</span>
            </div>
            <button
              onClick={() => setShowRenderPanel(false)}
              disabled={applyingLayout}
              aria-label="Close page render panel"
              className="p-1 rounded hover:bg-gray-200"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
            {/* Scope selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apply to
              </label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="renderScope"
                    disabled={applyingLayout}
                    checked={useAllPages}
                    onChange={() => setUseAllPages(true)}
                    className="accent-gray-900"
                  />
                  <span className="text-sm text-gray-700">All pages</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="renderScope"
                    disabled={applyingLayout}
                    checked={!useAllPages}
                    onChange={() => setUseAllPages(false)}
                    className="accent-gray-900"
                  />
                  <span className="text-sm text-gray-700">
                    Selected pages only
                    {!useAllPages && selectedIds.length > 0 && (
                      <span className="ml-1 text-gray-400">({selectedIds.length} selected)</span>
                    )}
                  </span>
                </label>
                {!useAllPages && selectedIds.length === 0 && (
                  <p className="text-xs text-amber-600">
                    No pages selected. Check the boxes in the table first.
                  </p>
                )}
              </div>
            </div>

            {showRenderPanel && <BulkLayoutTemplateApply pages={layoutTargetPages} language={pageLanguage}
              disabled={isProcessing} onBusyChange={setApplyingLayout} onApplied={adoptCopiedLayout} />}

            {/* Manual generate button */}
            <LoadingButton
              className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              onClick={() => {
                startManualGeneration();
              }}
              disabled={
                isProcessing || applyingLayout ||
                (!useAllPages && selectedIds.length === 0)
              }
              loadingText="Generating…"
            >
              <ImagePlay size={16} />
              Generate page renders
            </LoadingButton>

            <Link to="/layout-templates" className="text-xs text-primary">Manage layout templates →</Link>
          </div>
        </div>

        {/* Loading modal while generating */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center gap-4 max-w-xs w-full mx-4">
              <Loader2 size={40} className="text-gray-700 animate-spin" />
              <p className="text-gray-800 font-medium text-center">Generating page renders…</p>
              <p className="text-gray-500 text-sm text-center">This may take a moment. Please don't close the page.</p>
              <button
                onClick={() => {
                  setIsProcessing(false);
                  setStartBulk(false);
                }}
                className="text-xs text-gray-400 hover:text-gray-600 underline mt-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto overflow-y-visible rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-14 border-b border-r border-gray-200 px-3 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="w-[27%] border-b border-r border-gray-200 px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Page Name
                </th>
                <th className="w-[22%] border-b border-r border-gray-200 px-3 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Page design
                </th>
                <th className="w-[51%] border-b border-gray-200 px-5 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Texts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pages.map((page, index) => (
                <tr
                  key={page._id}
                  id={`page-row-${page.page_number}`}
                  className={`${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } transition-colors duration-500`}
                >
                  <td
                    className="cursor-pointer border-r border-gray-200 p-2 text-center align-top hover:bg-gray-100"
                    onClick={() => toggleSelectOne(page._id)}
                    title="Select this page"
                  >
                    <input
                      type="checkbox"
                      value={page._id}
                      checked={selectedIds.includes(page._id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleSelectOne(page._id)}
                    />
                  </td>
                  <td className="border-r border-gray-200 px-3 py-5 text-center align-top overflow-visible text-gray-700">
                    <Link
                      to={`/edit-preset/${page._id}?plang=${pageLanguage}`}
                      onClick={() => sessionStorage.setItem('returnToPage', page.page_number)}
                      className="text-sm font-medium text-gray-900 hover:text-primary"
                    >
                      {page.page_number}.{page.name}
                    </Link>
                    <div className="flex flex-col items-center mt-5 overflow-visible">
                      {page['image_preview_' + pageLanguage] ? (
                        <img
                          src={getImageUrl(
                            page['image_preview_' + pageLanguage],
                          )}
                          alt={`Page render ${pageLanguage}`}
                          className="h-72 w-52 object-cover border rounded mb-1 cursor-pointer transition-transform duration-300 hover:scale-[1.3] hover:shadow-2xl hover:z-50 relative"
                          onClick={() =>
                            window.open(
                              `${getImageUrl(page['image_preview_' + pageLanguage])}`,
                              '_blank',
                            )
                          }
                        />
                      ) : (
                        <img
                          src={`/uploads/${page.image_path}`}
                          alt="Original page artwork"
                          className="mb-2 h-72 w-52 object-cover border rounded cursor-pointer mx-auto transition-transform duration-300 hover:scale-[1.3] hover:shadow-2xl hover:z-50 relative"
                          onClick={() =>
                            window.open(`/uploads/${page.image_path}`, '_blank')
                          }
                        />
                      )}
                      <div className="flex flex-col items-center mt-4 mb-10 gap-2">
                        <Link
                          to={`/edit-preset/${page._id}?plang=${pageLanguage}`}
                          onClick={() => sessionStorage.setItem('returnToPage', page.page_number)}
                          className="flex items-center justify-center bg-white shadow px-8 py-2 text-xs rounded hover:bg-blue-200"
                        >
                          Edit &nbsp;
                          <Edit size={18} />
                        </Link>

                        <button
                          onClick={() => {
                            startManualGeneration([page]);
                          }}
                          disabled={isProcessing || applyingLayout}
                          className="bg-white shadow px-4 py-2 text-xs rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          Generate page render
                        </button>

                        <button
                          onClick={() => removePageRender(page._id)}
                          disabled={!page['image_preview_' + pageLanguage]}
                          className="bg-white shadow px-4 py-2 text-xs rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            page['image_preview_' + pageLanguage]
                              ? 'Remove page render and show original artwork'
                              : 'No page render to remove'
                          }
                        >
                          Remove page render
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="border-r border-gray-200 px-3 py-5 text-center align-top text-gray-700">
                    <div className="flex flex-col items-center gap-2.5">
                      {(pageLayoutBoxCounts[page._id] ?? page.page_layout_box_count ?? page.page_text_box_count ?? 0) > 0 ? (
                        <span title="Private layout saved on this page" className="inline-flex max-w-full items-center justify-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-center text-[11px] font-semibold leading-tight text-violet-800">
                          <Layers size={13} className="shrink-0" />
                          Private layout · {pageLayoutBoxCounts[page._id] ?? page.page_layout_box_count ?? page.page_text_box_count} saved {(pageLayoutBoxCounts[page._id] ?? page.page_layout_box_count ?? page.page_text_box_count) === 1 ? 'box' : 'boxes'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-500">{(copiedLayoutVersions[page._id] ?? page.layout_version) === 1 ? 'Empty private layout' : 'Layout needs review'}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-5 text-gray-700">
                      {pageLanguage
                      .split('_')
                      .map((langCode) => `text_${langCode}`)
                      .map((field) => (
                        <div key={field} className="mb-4">
                          <textarea
                            value={page[field] || ''}
                            rows="5"
                            className="w-full border border-gray-300 p-2 rounded mt-1"
                            onChange={(e) => {
                              const newText = e.target.value;
                              setPages((prevPages) =>
                                prevPages.map((p) =>
                                  p._id === page._id
                                    ? { ...p, [field]: newText }
                                    : p,
                                ),
                              );
                            }}
                          />
                          <div className="flex items-center mt-2">
                            <button
                              onClick={() =>
                                handleSaveText(page._id, field, page[field])
                              }
                              className="p-2 bg-white rounded shadow hover:bg-gray-100 flex justify-center items-center"
                            >
                              <Save size={20} />
                            </button>
                            <label className="ml-2 text-sm font-medium text-gray-700">
                              {languageFieldMap[field]}
                            </label>
                          </div>
                        </div>
                      ))}
                    {(additionalEntriesByPageId[page._id] || []).length > 0 && (
                      <div className="mt-2 space-y-4 border-t border-gray-200 pt-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Additional text</div>
                        {(additionalEntriesByPageId[page._id] || []).map((entry) => (
                          <div key={entry.id} className="border-t border-gray-200 pt-4 first:border-t-0 first:pt-0">
                            <div className="mb-2 text-sm font-semibold text-gray-900">{entry.label || 'Additional text'}</div>
                            {['en', ...pageLanguage.split('_').filter((language) => language !== 'en')].map((language) => (
                              <div key={`${entry.id}-${language}`} className="mb-4">
                                <textarea
                                  value={entry.texts?.[language] || ''}
                                  rows="5"
                                  className="mt-1 w-full rounded border border-gray-300 p-2 text-sm text-gray-700"
                                  onChange={(event) => updateAdditionalEntryText(page._id, entry.id, language, event.target.value)}
                                />
                                <div className="mt-2 flex items-center">
                                  <button
                                    type="button"
                                    onClick={() => saveAdditionalEntryLanguage(page._id, entry.id, language)}
                                    disabled={savingAdditionalFields[`${page._id}:${entry.id}:${language}`]}
                                    className="flex items-center justify-center rounded bg-white p-2 shadow hover:bg-gray-100 disabled:opacity-50"
                                    aria-label={`Save ${languageFieldMap[`text_${language}`]} additional text`}
                                    title={`Save ${languageFieldMap[`text_${language}`]} additional text`}
                                  >
                                    {savingAdditionalFields[`${page._id}:${entry.id}:${language}`] ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                                  </button>
                                  <label className="ml-2 text-sm font-medium text-gray-700">
                                    {languageFieldMap[`text_${language}`]}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <p className="text-center text-gray-600">Loading...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}
        {!loading && pages.length === 0 && (
          <p className="text-center text-gray-500">No pages available.</p>
        )}

        {startBulk && pagesToProcess.length > 0 && (
          <PreviewBatchGeneratorPresets
            pages={pagesToProcess}
            selectedLanguage={pageLanguage}
            onAllDone={() => {
              notify('Page renders saved.');
              setStartBulk(false);
              setIsProcessing(false);
              setPagesToProcess([]);

              setRefreshCounter((prev) => prev + 1);
            }}
            onError={(err) => {
              notify(`Generation stopped: ${err}`, 'error');
              setStartBulk(false);
              setIsProcessing(false);
              setPagesToProcess([]);

            }}
          />
        )}
      </div>
    </>
  );
}

export default BulkEditorPagePresets;
